import { isFunction, isString } from 'lodash';
import { IEnvGetter } from '~/types/dependency.types';
import { tryParseToJsonString } from '~/utils/json.utils';
import { FSApi } from '@fullstory/snippet';
import { parseEventNameForFS } from '~/services/fullstory/fullstory.utils';
import { ILogger } from '~/packages/core';

type logTypes = 'debug' | 'info' | 'warn' | 'error';

const MAX_MESSAGE_LENGTH = 10000;

interface IFullstoryServiceProps {
    envGetter: IEnvGetter;
}

function getFullStoryType(type: logTypes) {
    if (type === 'info') {
        return 'log';
    }
    return type;
}

function formatFullStoryMsg(resultMessage: string) {
    return resultMessage.substring(0, MAX_MESSAGE_LENGTH);
}

export class FullstoryService implements ILogger {
    private fullStory: FSApi;
    envGetter: IEnvGetter;
    isTracking: boolean = false;

    constructor({ envGetter }: IFullstoryServiceProps) {
        this.envGetter = envGetter;
    }

    init(fullStory: FSApi) {
        this.fullStory = fullStory;
    }

    consoleLog(
        type: logTypes,
        ...args: Parameters<(typeof console)[logTypes]>
    ) {
        // eslint-disable-next-line no-console
        console[type](...args);
    }

    getResultMessage(message: any) {
        if (message?.name === 'AxiosError') {
            return message.message;
        }

        return isFunction(message) ? message() : message;
    }

    log(type: logTypes, message: any) {
        let resultMessage;

        try {
            resultMessage = this.getResultMessage(message);
        } catch (e) {
            this.error(e);
            this.error('Failed generating log message');

            return;
        }

        if (
            !this.envGetter.isProduction() ||
            window.enableAnalyticsConsoleLogging
        ) {
            this.consoleLog(type, resultMessage);

            return;
        }

        if (!this.fullStory) {
            return;
        }

        resultMessage = isString(resultMessage)
            ? resultMessage
            : this.parseToString(resultMessage);

        this.fullStory('log', {
            level: getFullStoryType(type),
            msg: formatFullStoryMsg(resultMessage)
        });
    }

    trackFullstoryEvent({
        event,
        props = {}
    }: {
        event: string;
        props?: Record<string, any>;
    }) {
        this.debug(`[tracking: fullStory] ${event}, ${JSON.stringify(props)}`);

        if (!this.fullStory) {
            return;
        }
        const { action } = props;
        const { name, description } = parseEventNameForFS(event, action);
        const properties = { ...props, description };

        this.fullStory('trackEvent', {
            name,
            properties
        });
    }

    debug(message: any) {
        this.log('debug', message);
    }

    info(message: any) {
        this.log('info', message);
    }

    warn(message: any) {
        this.log('warn', message);
    }

    error(message: any) {
        this.log('error', message);
    }

    private parseToString(data): string {
        if (!data) {
            return data;
        }

        const { json: jsonStringData } = tryParseToJsonString(data);

        return jsonStringData;
    }
}
